<template>
  <div v-loading="isLoading" data-qa="bracket-builder-wrapper">
    <rest-form
      v-if="isCompetitionReady"
      ref="bracket-builder-form"
      :model="model"
      :disabled="isReadOnly"
      :save-endpoint="saveEndpoint"
      :after-save="afterSave"
      @on-error="isSaveLoading = false"
      data-qa="bracket-builder-form"
    >
      <form-section
        :title="$t('Gamification_BracketBuilder_NumberOfTeams')"
        :description="
          $t('Gamification_BracketBuilder_NumberOfTeamsDescription')
        "
        :is-required="true"
      >
        <rest-form-item
          prop="teamNumber"
          :label="$t('Gamification_Teams')"
          class="default-width"
          :rules="teamNumberRules"
        >
          <el-input
            v-model.number="model.teamNumber"
            required
            type="number"
            min="1"
            max="64"
            data-qa="bracket-builder-team-number-input"
            @input="buildTournamentStructure"
          />
        </rest-form-item>
      </form-section>

      <div v-if="rounds.length && !isLoadingBracket">
        <p class="title">
          {{ $t('Gamification_BracketBuilder_BracketStructure') }}
        </p>
        <p class="description">
          {{ $t('Gamification_BracketBuilder_BracketStructureDescription') }}
        </p>
        <div class="rounds-wrapper" data-qa="rounds-wrapper">
          <section
            v-for="(round, roundIndex) in rounds"
            :key="roundIndex"
            class="round-section"
            :data-qa="`round-${roundIndex + 1}`"
          >
            <p class="round-title">
              {{ $t('Gamification_Round') }} {{ roundIndex + 1 }}
            </p>
            <div class="games-wrapper">
              <div
                v-for="(game, gameIndex) in round"
                :key="game.game_id"
                class="game-item"
                :data-qa="`game-${game.game_id}`"
              >
                <p class="game-title">
                  {{ $t('Gamification_Game') }} {{ game.game_id }}
                </p>
                <div class="game-form-wrapper">
                  <rest-form-item
                    class="flex-1 full-width"
                    :prop="`structure.${roundIndex}.${gameIndex}.game_day`"
                    :label="$t('Gamification_BracketBuilder_GameDay')"
                    :rules="gameDayRules(roundIndex)"
                  >
                    <el-date-picker
                      v-model="model.structure[roundIndex][gameIndex].game_day"
                      type="date"
                      value-format="yyyy-MM-dd"
                      format="MMMM d yyyy"
                      :picker-options="startPickerOptions"
                      required
                      :data-qa="`game-day-${gameIndex}`"
                    />
                  </rest-form-item>

                  <rest-form-item
                    :label="`${$t('Gamification_Team')} 1`"
                    :prop="`structure.${roundIndex}.${gameIndex}.team1_id`"
                    :rules="gameTeam1Rules(game)"
                    class="full-width"
                  >
                    <el-select
                      v-if="!game.team1_winner_of"
                      v-model="model.structure[roundIndex][gameIndex].team1_id"
                      filterable
                      clearable
                      :data-qa="`game-${gameIndex}-team-1-select`"
                      @change="handleTeamChange(round, roundIndex)"
                    >
                      <el-option
                        v-for="{ value, label } in filteredTeams"
                        :key="value"
                        :value="value"
                        :label="label"
                      />
                    </el-select>

                    <el-input
                      v-else
                      :value="
                        `${$t('Gamification_WinnerOfGame')} ${
                          game.team1_winner_of
                        }`
                      "
                      disabled
                      :data-qa="`game-${gameIndex}-team-1-winner-of`"
                    />
                  </rest-form-item>

                  <rest-form-item
                    :label="`${$t('Gamification_Team')} 2`"
                    :prop="`structure.${roundIndex}.${gameIndex}.team2_id`"
                    :rules="gameTeam2Rules(game)"
                    class="full-width"
                  >
                    <el-select
                      v-if="!game.team2_winner_of"
                      v-model="model.structure[roundIndex][gameIndex].team2_id"
                      filterable
                      clearable
                      :data-qa="`game-${gameIndex}-team-2-select`"
                    >
                      <el-option
                        v-for="{ value, label } in filteredTeams"
                        :key="value"
                        :value="value"
                        :label="label"
                      />
                    </el-select>

                    <el-input
                      v-else
                      :value="
                        `${$t('Gamification_WinnerOfGame')} ${
                          game.team2_winner_of
                        }`
                      "
                      disabled
                      :data-qa="`game-${gameIndex}-team-2-winner-of`"
                    />
                  </rest-form-item>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </rest-form>

    <div
      v-else-if="!isLoading"
      class="bracket-not-ready"
      data-qa="bracket-builder-not-ready"
    >
      <p>{{ $t('Gamification_BracketBuilder_NotReady') }}</p>
    </div>

    <div v-if="isCreationMode" class="footer">
      <div class="footer-ct">
        <el-button
          type="primary"
          :disabled="isSubmitButtonDisabled"
          @click="saveBracketBuilder"
          data-qa="next-step-btn"
        >
          {{ getNextStepLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </div>
    </div>
  </div>
</template>
<script>
import RestForm from '@/common/components/rest/RestForm'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import moment from 'moment/moment'
import {
  getLatestDateFromRounds,
  getEarliestDateFromRounds
} from '@/modules/gamification/helpers'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'BracketBuilderForm',
  components: {
    RestForm,
    RestFormItem,
    FormSection
  },
  props: {
    campaign: {
      type: Object,
      required: true
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    },
    saveFlag: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      step: GameCreationSubSteps.BRACKET_BUILDER,
      timeoutId: null,
      isCompetitionReady: false,
      isLoadingCompetition: false,
      isLoadingTeams: false,
      isLoadingBracket: false,
      isSaveLoading: false,
      bracketId: null,
      model: {
        teamNumber: undefined,
        structure: []
      },
      teams: [],
      unassignedWinners: [],
      byes: 0
    }
  },
  computed: {
    hasSavedBracket() {
      return !!(this.campaign && this.campaign.has_bracket)
    },
    isLoading() {
      return (
        this.isLoadingCompetition ||
        this.isLoadingTeams ||
        this.isLoadingBracket ||
        this.isSaveLoading
      )
    },
    isSubmitButtonDisabled() {
      return this.isLoading || !this.isCompetitionReady
    },
    rounds() {
      return this.model.structure
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'POST',
        url: `gamify/${this.$route.params.id}/save-manual-bracket/${this.gameCampaignId}`
      })
    },
    filteredTeams() {
      if (this.bracketId) {
        return this.teams
      }

      const selectedTeams = this.model.structure
        .flatMap(round => round.flatMap(game => [game.team1_id, game.team2_id]))
        .filter(Boolean)

      return this.teams.filter(team => !selectedTeams.includes(team.value))
    },
    isReadOnly() {
      return !!this.bracketId
    },
    teamNumberRules() {
      return [
        {
          required: true,
          message: this.$t('Gamification_BracketBuilder_NumberOfTeamsRequired'),
          trigger: 'change'
        },
        {
          validator: (_rule, value, callback) => {
            if (value === null || value === undefined || value === '') {
              return callback()
            }

            const number = Number(value)

            if (Number.isNaN(number)) {
              return callback(
                new Error(
                  this.$t(
                    'Gamification_BracketBuilder_NumberOfTeamsValidNumber'
                  )
                )
              )
            }

            if (number <= 0) {
              return callback(
                new Error(
                  this.$t(
                    'Gamification_BracketBuilder_NumberOfTeamsPositiveNumber'
                  )
                )
              )
            }

            if (number > 64) {
              return callback(
                new Error(
                  this.$t(
                    'Gamification_BracketBuilder_NumberOfTeamsMaxValidation'
                  )
                )
              )
            }

            if (number % 2 !== 0) {
              return callback(
                new Error(
                  this.$t('Gamification_BracketBuilder_NumberOfTeamsEvenNumber')
                )
              )
            }

            return callback()
          },
          trigger: 'change'
        }
      ]
    },
    gameDayRules() {
      return roundIndex => [
        {
          required: true,
          message: this.$t('Gamification_BracketBuilder_GameDayRequired'),
          trigger: 'change'
        },
        {
          validator: (_rule, value, callback) => {
            if (!value) return callback()

            const currentDate = moment(value)

            const campaignStart = this.campaign.starts_at
              ? moment.parseZone(this.campaign.starts_at)
              : null

            const campaignEnd = this.campaign.ends_at
              ? moment.parseZone(this.campaign.ends_at)
              : null

            if (
              (campaignStart && currentDate.isBefore(campaignStart, 'day')) ||
              (campaignEnd && currentDate.isAfter(campaignEnd, 'day'))
            ) {
              return callback(
                new Error(
                  this.$t(
                    'Gamification_BracketBuilder_GameDayWithinCampaignDateRange'
                  )
                )
              )
            }

            const previousRounds = this.rounds.slice(0, roundIndex)
            const latestPreviousDate = getLatestDateFromRounds(previousRounds)

            if (
              latestPreviousDate &&
              currentDate.isSameOrBefore(latestPreviousDate, 'day')
            ) {
              return callback(
                new Error(
                  `${this.$t(
                    'Gamification_BracketBuilder_GameDayBeforeValidation'
                  )} ${latestPreviousDate.format('DD.MM.yyyy')}`
                )
              )
            }

            const nextRounds = this.rounds.slice(roundIndex + 1)
            const earliestNextDate = getEarliestDateFromRounds(nextRounds)

            if (
              earliestNextDate &&
              currentDate.isSameOrAfter(earliestNextDate, 'day')
            ) {
              return callback(
                new Error(
                  `${this.$t(
                    'Gamification_BracketBuilder_GameDayAfterValidation'
                  )} ${earliestNextDate.format('DD.MM.yyyy')}`
                )
              )
            }

            return callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    gameTeam1Rules() {
      return game => [
        {
          validator: (_rule, _value, callback) => {
            if (!game.team1_winner_of && !game.team1_id) {
              return callback(
                new Error(this.$t('Gamification_BracketBuilder_Team1Required'))
              )
            }

            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    gameTeam2Rules() {
      return game => [
        {
          validator: (_rule, _value, callback) => {
            if (!game.team2_winner_of && !game.team2_id) {
              return callback(
                new Error(this.$t('Gamification_BracketBuilder_Team2Required'))
              )
            }

            callback()
          },
          trigger: ['blur', 'change']
        }
      ]
    },
    startPickerOptions() {
      return {
        disabledDate: date => {
          const today = moment().startOf('day')
          return moment(date).isBefore(today, 'day')
        }
      }
    },
    hasChanges() {
      if (this.bracketId || this.isLoading) {
        return false
      }

      return this.model.teamNumber !== undefined
    },
    getNextStepLabel() {
      return this.bracketId
        ? this.$t('Gamification_GameCompetitionTeamsForm_ContinueButton')
        : this.$t('Gamification_GameCompetitionTeamsForm_SaveButton')
    }
  },
  async mounted() {
    if (this.hasSavedBracket) {
      this.loadTeams()
      this.getBracketStructure()
      this.isCompetitionReady = true
    } else {
      this.startPolling()
    }
  },
  watch: {
    saveFlag: {
      handler() {
        if (this.bracketId) {
          return this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        }

        this.saveBracketBuilder()
      }
    },
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    isSubmitButtonDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    }
  },
  methods: {
    getBracketStructure() {
      if (!this.gameCampaignId) return

      this.isLoadingBracket = true

      const endpoint = projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-manual-bracket/${this.gameCampaignId}`
      })

      RestApi(endpoint)
        .then(({ data }) => {
          if (data && data.data) {
            this.bracketId = data.data.id

            const teams = data.data.bracket
              .flatMap(round =>
                round.flatMap(game => [game.team1_id, game.team2_id])
              )
              .filter(Boolean)

            this.model.teamNumber = teams.length

            this.model.structure = data.data.bracket
          }
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          this.isLoadingBracket = false
        })
    },

    startPolling() {
      this.isLoadingCompetition = true

      let delay = 2000
      const MAX_DELAY = 30000
      const MAX_DURATION = 90000
      const startTime = Date.now()
      const endpoint = projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-competition-ready/${this.gameCampaignId}`
      })

      const poll = async () => {
        try {
          const { data } = await RestApi(endpoint)

          if (data === 1) {
            this.isCompetitionReady = true
            this.loadTeams()
            return this.stopPolling()
          }

          if (Date.now() - startTime >= MAX_DURATION) {
            return this.stopPolling()
          }

          delay = Math.min(delay * 2, MAX_DELAY)
          this.timeoutId = setTimeout(poll, delay)
        } catch (error) {
          this.stopPolling()
          this.$message({
            message: error.message,
            type: 'error'
          })
        }
      }

      poll()
    },

    stopPolling() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
        this.timeoutId = null
      }
      this.isLoadingCompetition = false
    },

    loadTeams() {
      this.isLoadingTeams = true
      const endpoint = projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-manual-bracket-participants/${this.gameCampaignId}`
      })

      RestApi(endpoint)
        .then(({ data }) => {
          const response = data['data']

          if (response) {
            this.teams = response.map(item => {
              return {
                label: item.name,
                value: item.id
              }
            })
          }
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.isLoadingTeams = false
        })
    },

    buildTournamentStructure(teamCount) {
      if (teamCount < 2 || teamCount % 2 !== 0) {
        return (this.model.structure = [])
      }

      const totalRounds = Math.ceil(Math.log2(teamCount))
      const bracketSize = Math.pow(2, totalRounds)
      this.byes = bracketSize - teamCount

      const rounds = []
      let gameId = 1

      // First round
      const firstRoundMatches = (teamCount - this.byes) / 2
      const firstRound = []
      let winnersQueue = []

      for (let i = 0; i < firstRoundMatches; i++) {
        const game = {
          game_day: null,
          game_id: gameId,
          team1_id: null,
          team1_winner_of: null,
          team2_id: null,
          team2_winner_of: null
        }

        firstRound.push(game)
        winnersQueue.push(gameId)
        gameId++
      }

      rounds.push(firstRound)

      if (teamCount > 2) {
        // Second round
        const secondRoundTeams = firstRoundMatches + this.byes
        const secondRoundMatches = secondRoundTeams / 2
        const secondRound = []
        const secondRoundWinnersQueue = []

        for (let i = 0; i < secondRoundMatches; i++) {
          const game = {
            game_day: null,
            game_id: gameId,
            team1_id: null,
            team1_winner_of: this.byes === 0 ? winnersQueue.shift() : null,
            team2_id: null,
            team2_winner_of: winnersQueue.length ? winnersQueue.shift() : null
          }

          secondRound.push(game)
          secondRoundWinnersQueue.push(gameId)
          gameId++
        }

        // Winners from round 1 that advance via byes
        this.unassignedWinners = [...winnersQueue]

        rounds.push(secondRound)
        winnersQueue = [...secondRoundWinnersQueue]

        // Rest rounds
        for (let round = 3; round <= totalRounds; round++) {
          const prevWinners = [...winnersQueue]
          winnersQueue.length = 0

          const roundGames = []

          for (let i = 0; i < prevWinners.length; i += 2) {
            const game = {
              game_day: null,
              game_id: gameId,
              team1_id: null,
              team1_winner_of: prevWinners[i],
              team2_id: null,
              team2_winner_of: prevWinners[i + 1]
            }

            roundGames.push(game)
            winnersQueue.push(gameId)
            gameId++
          }

          rounds.push(roundGames)
        }
      }

      this.model.structure = rounds
    },

    handleTeamChange(round, roundIndex) {
      if (!round || roundIndex !== 1 || !this.unassignedWinners.length) return

      const selectedTeamIds = round
        .flatMap(game => [game.team1_id, game.team2_id])
        .filter(Boolean)

      if (selectedTeamIds.length < this.byes) {
        round.forEach(game => {
          game.team1_winner_of = null
        })
      } else if (selectedTeamIds.length === this.byes) {
        let winnerIndex = 0

        round.forEach((game, index) => {
          if (
            (game.team1_id === null || game.team1_id === '') &&
            winnerIndex < this.unassignedWinners.length
          ) {
            game.team1_winner_of = this.unassignedWinners[winnerIndex]

            const field = `structure.${roundIndex}.${index}.team1_id`
            this.$refs['bracket-builder-form'].validateField(field)

            winnerIndex++
          }
        })
      }
    },

    async saveBracketBuilder() {
      if (this.bracketId) {
        return this.$emit('goToNextStep', this.step)
      }

      this.isSaveLoading = true

      try {
        const valid = await this.$refs['bracket-builder-form'].validate()

        if (valid) {
          this.$refs['bracket-builder-form'].save()
        }
      } catch (e) {
        console.error(e)
        this.isSaveLoading = false
      }
    },

    afterSave(_data, finishLoading) {
      finishLoading()
      this.isSaveLoading = false

      EventBus.$emit('updateCampaign', {
        has_bracket: true
      })

      this.$emit('goToNextStep', this.step)
    }
  }
}
</script>
<style scoped>
.default-width {
  width: 255px;
}

.rounds-wrapper {
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 8px;
}

.round-section {
  padding: 24px;
  border: 1px solid #ebeef5;
  box-shadow: 0px 4px 8px 0px #4b515f0d;
  background: #f8fafc;
  border-radius: 4px;
}

.games-wrapper {
  display: flex;
  flex-direction: column;
  gap: 35px;
  margin-top: 10px;
}

.game-item {
  padding: 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #dbdee7;
}

.title {
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #4b515f;
}

.description {
  font-size: 12px;
  line-height: 100%;
  color: #5c6374;
}

.round-title,
.game-title {
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  color: #4b515f;
}

.game-form-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}

.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

.full-width {
  width: 100%;
}

.bracket-not-ready {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 50px;
  color: #4b515f;
  font-size: 20px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/setup/common/BracketBuilderForm.vue